import dayjs, { ManipulateType, OpUnitType } from "dayjs";
import { useMemo } from "react";
import {
  FilterData,
  Purchase,
  RouteAnalysis,
  TopRoutes,
  TotalSpent,
} from "~/application/types/entities/CustomerBI.type";
import { SvgArrowDown, SvgArrowUp } from "~/components/Icon/icons";
import { displayDate } from "~/utils/date.utils";
import { asCurrency } from "~/utils/mask.utils";
import { ItemService } from "./types";

export enum FilterKeyType {
  DAY = "day",
  WEEK = "week",
  MONTH = "month",
  THREE_MONTHS = "threeMonths",
  YEAR = "year",
}

export type ItemValue = {
  roadValue: number;
  airwayValue: number;
  hotelValue: number;
  vehicleValue: number;
  totalValue: number;
};

export type ConvertedDataItem = {
  name: string;
  sales: number;
  amt: number;
};

type ConvertedData = {
  year: ConvertedDataItem[];
  month: ConvertedDataItem[];
  threeMonths: ConvertedDataItem[];
  week: ConvertedDataItem[];
  twoMonths: ConvertedDataItem[];
  day: ConvertedDataItem[];
};

export function calculatePercentage(value: number, total: number): number {
  return Math.round((value / total) * 100);
}

export function getPercentageByItem({
  airwayValue,
  hotelValue,
  roadValue,
  totalValue,
  vehicleValue,
}: ItemValue) {
  const percentages = {
    road: calculatePercentage(roadValue, totalValue),
    airway: calculatePercentage(airwayValue, totalValue),
    hotel: calculatePercentage(hotelValue, totalValue),
    vehicle: calculatePercentage(vehicleValue, totalValue),
  };

  return [
    {
      name: "Rodoviário",
      item: "road",
      value: percentages.road,
    },
    {
      name: "Aéreo",
      item: "airway",
      value: percentages.airway,
    },
    {
      name: "Hotel",
      item: "hotel",
      value: percentages.hotel,
    },
    {
      name: "Carro",
      item: "vehicle",
      value: percentages.vehicle,
    },
  ];
}

export const topRoutes = [
  { header: "Origem", value: (route?: TopRoutes) => route?.departure, isNumeric: true },
  { header: "Destino", value: (route?: TopRoutes) => route?.arrival, isNumeric: false },
  {
    header: "Ticket médio",
    isNumeric: true,
    value: (route?: TopRoutes) => asCurrency(route?.average_ticket || 0),
  },
  {
    header: "Valor",
    isNumeric: true,
    value: (route?: TopRoutes) => asCurrency(route?.value || 0),
  },
];

export const secondTablebLastPurchase = [
  {
    header: "Antecedência",
    isNumeric: true,
    value: (purchase?: RouteAnalysis) => purchase?.leadTime,
  },
  {
    header: "Freq.",
    isNumeric: false,
    value: (purchase?: RouteAnalysis) => purchase?.frequency,
  },
  {
    header: "Ticket M.",
    isNumeric: true,
    value: (purchase?: RouteAnalysis) => purchase?.averageTicket,
  },
  {
    header: "Share",
    value: (purchase?: RouteAnalysis) => `${((purchase?.share || 0) * 100).toFixed(0)}%`,
    isNumeric: true,
  },
  {
    header: "Valor",
    isNumeric: true,
    value: (purchase?: RouteAnalysis) => asCurrency(purchase?.value || 0),
  },
];

export function getLast12MonthsRange(): { startDate: string; endDate: string } {
  const endDate = dayjs().endOf("month").toISOString();
  const startDate = dayjs().startOf("month").subtract(11, "months").toISOString();
  return { startDate, endDate };
}

export function getLast3MonthsRange(): { startDate: string; endDate: string } {
  const endDate = dayjs().endOf("month").toISOString();
  const startDate = dayjs().startOf("month").subtract(2, "months").toISOString();
  return { startDate, endDate };
}

export function getLast30DaysRange(): { startDate: string; endDate: string } {
  const endDate = dayjs().toISOString();
  const startDate = dayjs().subtract(30, "days").toISOString();
  return { startDate, endDate };
}

export function getLast7DaysRange(): { startDate: string; endDate: string } {
  const endDate = dayjs().toISOString();
  const startDate = dayjs().subtract(7, "days").toISOString();
  return { startDate, endDate };
}

export function getLastDayRange(): { startDate: string; endDate: string } {
  const endDate = dayjs().toISOString();
  const startDate = dayjs().subtract(1, "day").toISOString();
  return { startDate, endDate };
}

export function getDateRange(type: "month" | "year") {
  const range = { month: getLast3MonthsRange(), year: getLast12MonthsRange() };

  return {
    startDate: range[type].startDate,
    endDate: range[type].endDate,
  };
}

export function getDateRangeByPeriod({ type }: { type: FilterKeyType }): {
  startDate: string;
  endDate: string;
} {
  switch (type) {
    case FilterKeyType.YEAR:
      return getLast12MonthsRange();
    case FilterKeyType.THREE_MONTHS:
      return getLast3MonthsRange();
    case FilterKeyType.MONTH:
      return getLast30DaysRange();
    case FilterKeyType.WEEK:
      return getLast7DaysRange();
    case FilterKeyType.DAY:
      return getLastDayRange();
    default:
      throw new Error(`Unsupported period type: ${type}`);
  }
}

export const filters = [
  {
    type: FilterKeyType.YEAR,
    value: "12 meses",
    ...getDateRangeByPeriod({ type: FilterKeyType.YEAR }),
  },
  {
    type: FilterKeyType.THREE_MONTHS,
    value: "3 meses",
    ...getDateRangeByPeriod({ type: FilterKeyType.THREE_MONTHS }),
  },
];

export const getPercentValueIcon = (isUp: boolean) => {
  switch (isUp) {
    case false:
      return SvgArrowUp;
    case true:
      return SvgArrowDown;
  }
};

export function mapToEntries(totalSpent?: TotalSpent[]) {
  return totalSpent?.map((spent) => ({
    type: spent?.type,
    value: spent?.value,
    data: spent?.data,
  }));
}
export function convertChartData(jsonData?: TotalSpent[]): ConvertedData {
  const result: ConvertedData = {
    year: [],
    month: [],
    threeMonths: [],
    twoMonths: [],
    week: [],
    day: [],
  };

  jsonData?.forEach((entry) => {
    const period = entry.type;
    const data = entry?.data?.map((item) => ({
      name:
        period === "day"
          ? `${item.hour}`
          : period === "week" && entry.value === 7
          ? `${item.day}`
          : period === "month" && entry.value === 30
          ? `${new Date(item.date).getDate()} ${new Date(item.date).toLocaleString("pt-BR", {
              month: "short",
            })}`
          : period === "three-months" && entry.value === 3
          ? `${item.month}`
          : period === "year"
          ? `${item.month}/${item.year.toString().slice(-2)}`
          : "",
      sales: item.value,
      amt: item.value,
      date: item.date,
    }));

    if (period === "year" && entry.value === 12) {
      result.year = data;
    } else if (period === "three-months" && entry.value === 3) {
      result.threeMonths = data;
    } else if (period === "two-months") {
      result.twoMonths = data;
    } else if (period === "month" && entry.value === 30) {
      result.month = data;
    } else if (period === "week" && entry.value === 7) {
      result.week = data;
    } else if (period === "day") {
      result.day = data;
    }
  });

  return result;
}

const services: { [key: string]: string } = {
  [ItemService.ROAD]: "road",
  [ItemService.AIRWAY]: "airway",
  [ItemService.HOTEL]: "hotel",
  [ItemService.VEHICLE]: "vehicle",
};

export function getService(service: string): string | undefined {
  return services[service.toLowerCase()];
}

export const parseChartStartDate = ({ dateType, date }: { dateType: OpUnitType; date: Date }) =>
  dayjs(date).startOf(dateType).toISOString();

export const parseChartEndDate = ({ dateType, date }: { dateType: OpUnitType; date: Date }) =>
  dayjs(date).endOf(dateType).toISOString();

export const parseFutureDate = ({ dateType, date }: { dateType: ManipulateType; date: Date }) =>
  dayjs(date).add(2, dateType).endOf(dateType).toISOString();
